import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  DASHBOARD_DETAIL_PATH,
  DASHBOARD_PATH,
  DASHBOARD_V2_DETAIL_PATH,
  HOME_PATH,
  DASHBOARD_SURMOUNT_MANAGE_PATH,
} from "features/Dashboard/routes";
import {
  SIGNIN_PATH,
  SIGNUP_PATH,
  FORGOT_PASS_PATH,
  PASSWORD_RESET_SUCCESS_PATH,
  PASSWORD_RESET_COMPLETE_PATH,
  VERIFY_PHONE_PATH,
  WELCOME_QUESTIONS_PATH,
  WELCOME_CONNECT_PATH,
  WELCOME_CONNECTED_PATH,
  CHECK_EMAIL_PATH,
  WELCOME_PATH,
  SIGNUP_INVESTMENT_STYLE_DEFINING,
  SIGNUP_BUILD_PORTFOLIO,
  SIGNUP_INVESTMENT_APPROACH,
  SIGNUP_CONNECT_BROKER_ACCOUNT,
  SIGNUP_CONNECTED_BROKER_ACCOUNT,
  SIGNUP_GET_STARTED,
  SIGNUP_CHOOSE_FUNDING_METHOD,
  SIGNUP_CREATE_SURMOUNT_ACCOUNT,
  SIGNUP_SURMOUNT_ACCOUNT_ADDRESS,
  SIGNUP_SURMOUNT_ACCOUNT_COUNTRY_CITIZENSHIP,
  SIGNUP_SURMOUNT_RECOMMENDATIONS,
  SIGNUP_SURMOUNT_ACCOUNT_EMPLOYEE_STATUS,
  SIGNUP_SURMOUNT_ACCOUNT_ANNUAL_INCOME,
  SIGNUP_SURMOUNT_ACCOUNT_FUNDING_SOURCE,
  SIGNUP_SURMOUNT_ACCOUNT_NET_WORTH,
  SIGNUP_SURMOUNT_ACCOUNT_AFFILIATION,
  SIGNUP_SURMOUNT_ACCOUNT_VERIFY_IDENTITY,
  SIGNUP_SURMOUNT_ACCOUNT_TAX_W9_AGREEMENT,
  SIGNUP_SURMOUNT_ACCOUNT_STATUS,
  SIGNUP_INTRO_QUESTIONS,
  SIGNUP_DEPOSIT_FUNDS,
  SIGNUP_DEPOSIT_SUCCESS,
  SIGNUP_SURMOUNT_ACCOUNT_PERSONAL_DETAILS,
  SIGNUP_SURMOUNT_ACCOUNT_PHONE_VERIFICATION,
  SIGNUP_CHOOSE_STRATEGIES,
  SIGNUP_SURMOUNT_RECOMMENDATIONS_KYC_QUESTIONS,
} from "features/Auth/routes";
import {
  MARKETPLACE_PATH,
  MARKETPLACE_STRATEGY_DETAIL_PATH,
  MARKETPLACE_V2_COLLECTION_PATH,
  MARKETPLACE_V2_PATH,
  MARKETPLACE_V2_STRATEGY_DETAIL_PATH,
} from "features/Explore/routes";
import {
  STRATEGY_DETAIL_PATH,
  STRATEGY_EDIT_PATH,
  STRATEGY_PATH,
  STRATEGY_HOST_PATH,
  STRATEGY_HOST_PROGRESS_PATH,
  STRATEGY_HOST_FORM_PATH,
  ETF_BUILDER_PATH,
  STRATEGY_V2_DETAIL_PATH,
  STRATEGY_V2_EDIT_PATH,
  STRATEGY_V2_PATH,
  ETF_V2_BUILDER_PATH,
} from "features/Strategy/routes";
import {
  ACCOUNT_NOTIFICATION_PATH,
  ACCOUNT_PAYMENT_PATH,
  ACCOUNT_PLAN_BILLING_PATH,
  ACCOUNT_TWO_FACTOR_AUTHENTICATION_PATH,
  ACCOUNT_PLAN_DETAIL_PATH,
  ACCOUNT_OVERVIEW_PATH,
  ACCOUNT_SECURITY_PATH,
  ACCOUNT_OVERVIEW_V2_PATH,
  ACCOUNT_PLAN_BILLING_V2_PATH,
  ACCOUNT_SECURITY_V2_PATH,
  ACCOUNT_PARTNERSHIPS_V2_PATH,
} from "features/Account/routes";
import {
  MARKET_V2_PATH,
  STOCK_SEARCH_PATH,
  STOCK_SEARCH_DETAILS_PATH,
  STOCK_MOVERS_PATH,
  STOCK_FEAR_GREED_INDEX_PATH,
  MARKET_DETAILS_V2_PATH,
  MARKET_MOVERS_V2_PATH,
  MARKET_FEAR_GREED_INDEX_V2_PATH,
} from "features/Market/routes";
import { RESEARCH_PATH, STOCK_DETAIL_PATH } from "features/Research/routes";
import {
  HELP_CENTER_PATH,
  HELP_CENTER_V2_PATH,
} from "features/HelpCenter/routes";
import {
  NOTIFICATION_PATH,
  NOTIFICATION_V2_PATH,
} from "features/Notification/routes";
import { HOST_DASHBOARD_PATH, HOST_DETAIL_PATH } from "features/Host/routes";

import { contains } from "./index";
import { isChildRoute } from "utils";
import { trackPage } from "./tracking";
import { CURRENT_PATH_KEY } from "./constants";
import { PLANS_PATH } from "features/Plans/routes";
import { getItemFromStorage, setItemInStorage } from "./storage";
import {
  SURMOUNT_ACCOUNT_CREATE_PATH,
  SURMOUNT_ACCOUNT_PATH,
} from "features/SurmountAccount/routes";

const WHITELIST_PATH = ["strategy", "marketplace"];
const BLACKLIST_PATH = ["etf", "ncb"];

export function useNavigation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const currentParams = Object.fromEntries([...searchParams]);
    const parsedSearchParams = new URLSearchParams(currentParams).toString();

    const { pathname } = location;

    const isWhitelisted = WHITELIST_PATH.some((path) =>
      contains(pathname, path)
    );

    const isBlacklisted = BLACKLIST_PATH.some((path) =>
      contains(pathname, path)
    );

    if (isWhitelisted && !isBlacklisted && parsedSearchParams) {
      const currentPathWithParams = `${pathname}?${parsedSearchParams}`;
      setItemInStorage(CURRENT_PATH_KEY, currentPathWithParams);
    }
  }, [searchParams]);

  const navigateWithTracking = (path, options) => {
    const _path = `/v1${path}`;
    trackPage({ page: _path });

    if (typeof _path === "string" && !isChildRoute(_path)) {
      setItemInStorage(CURRENT_PATH_KEY, _path);
    }
    if (options) navigate(_path, options);
    else navigate(_path);
  };

  const navigateV2WithTracking = (path, options) => {
    trackPage({ page: path });

    if (typeof path === "string" && !isChildRoute(path)) {
      setItemInStorage(CURRENT_PATH_KEY, path);
    }
    if (options) navigate(path, options);
    else navigate(path);
  };

  // Marketplace
  const navigateToMarketplace = () => {
    navigateWithTracking(MARKETPLACE_PATH);
  };

  const navigateToV2Marketplace = () => {
    if (MARKETPLACE_V2_PATH !== location.pathname) {
      navigateV2WithTracking(MARKETPLACE_V2_PATH);
    }
  };

  const navigateToMarketplaceDetail = (id) => {
    navigateWithTracking(MARKETPLACE_STRATEGY_DETAIL_PATH.replace(":id", id));
  };

  const navigateToV2MarketplaceDetail = (id) => {
    navigateV2WithTracking(
      MARKETPLACE_V2_STRATEGY_DETAIL_PATH.replace(":id", id)
    );
  };

  const navigateToV2Market = () => {
    if (MARKET_V2_PATH !== location.pathname) {
      navigateV2WithTracking(MARKET_V2_PATH);
    }
  };

  const navigateToV2Research = () => {
    if (RESEARCH_PATH !== location.pathname) {
      navigateV2WithTracking(RESEARCH_PATH);
    }
  };

  const navigateToCollectionDetail = (slug, options = {}) => {
    navigateV2WithTracking(
      MARKETPLACE_V2_COLLECTION_PATH.replace(":slug", slug),
      options
    );
  };

  // Dashboard
  const navigateToDashboard = () => {
    navigateWithTracking(DASHBOARD_PATH);
  };

  const navigateToPortfolioDetail = (id, options) => {
    navigateWithTracking(DASHBOARD_DETAIL_PATH.replace(":id", id), options);
  };

  const navigateToV2PortfolioDetail = (id, options) => {
    navigateV2WithTracking(
      DASHBOARD_V2_DETAIL_PATH.replace(":id", id),
      options
    );
  };

  // Strategy
  const navigateToStrategyList = (param) => {
    if (param) {
      navigateWithTracking(`${STRATEGY_PATH}?${param}`);
    } else {
      navigateWithTracking(STRATEGY_PATH);
    }
  };

  const navigateToV2StrategyList = (param) => {
    if (param) {
      navigateV2WithTracking(`${STRATEGY_V2_PATH}?${param}`);
    } else {
      if (STRATEGY_V2_PATH !== location.pathname) {
        navigateV2WithTracking(STRATEGY_V2_PATH);
      }
    }
  };

  const navigateToV2StrategyDetail = (id) => {
    navigateV2WithTracking(STRATEGY_V2_DETAIL_PATH.replace(":id", id));
  };

  const navigateToStrategyDetail = (id) => {
    navigateWithTracking(STRATEGY_DETAIL_PATH.replace(":id", id));
  };

  const navigateToStrategyEdit = (id) => {
    navigateWithTracking(STRATEGY_EDIT_PATH.replace(":id", id));
  };

  const navigateToV2StrategyEdit = (id) => {
    navigateV2WithTracking(STRATEGY_V2_EDIT_PATH.replace(":id", id));
  };

  const navigateToNoCodeBuilder = (options) => {
    navigateWithTracking(options);
  };

  const navigateToV2NoCodeBuilder = (options) => {
    navigateV2WithTracking(options);
  };

  const navigateToEtfBuilder = (id) => {
    navigateWithTracking(ETF_BUILDER_PATH.replace(":id", id));
  };

  const navigateToV2EtfBuilder = (id, options = {}) => {
    let path = ETF_V2_BUILDER_PATH.replace(":id", id);
    // Support query string via `search` or key/value via `query`
    if (options?.search) {
      const search = options.search.startsWith("?")
        ? options.search.substring(1)
        : options.search;
      path = `${path}?${search}`;
    } else if (options?.query && typeof options.query === "object") {
      const params = new URLSearchParams(options.query);
      const qs = params.toString();
      if (qs) {
        path = `${path}?${qs}`;
      }
    }
    navigateV2WithTracking(path, options);
  };

  const navigateToNoCodeBuilderEdit = (options) => {
    navigateWithTracking(options);
  };

  const navigateToV2NoCodeBuilderEdit = (options) => {
    navigateV2WithTracking(options);
  };

  // Payment
  const navigateToAccountPaymentPlanDetail = (id, options) => {
    navigateWithTracking(ACCOUNT_PLAN_DETAIL_PATH.replace(":id", id), options);
  };

  // Auth
  const navigateToSignin = () => {
    navigateWithTracking(SIGNIN_PATH);
  };

  const navigateToV2Signin = () => {
    navigateV2WithTracking(SIGNIN_PATH);
  };

  const navigateToSignup = (options) => {
    navigateWithTracking(options || SIGNUP_PATH);
  };

  const navigateToV2Signup = (options) => {
    navigateV2WithTracking(options || SIGNUP_PATH);
  };

  const navigateToVerifyPhone = (options) => {
    navigateWithTracking(options || VERIFY_PHONE_PATH);
  };

  const navigateToV2VerifyPhone = (options) => {
    navigateV2WithTracking(options || VERIFY_PHONE_PATH);
  };

  const navigateToWelcome = () => {
    navigateWithTracking(WELCOME_PATH);
  };
  const navigateToV2Welcome = () => {
    navigateV2WithTracking(WELCOME_CONNECT_PATH);
  };

  const navigateToV2WelcomeConnected = () => {
    navigateV2WithTracking(WELCOME_CONNECTED_PATH);
  };

  const navigateToV2WelcomeQuestions = () => {
    navigateV2WithTracking(WELCOME_QUESTIONS_PATH);
  };

  const navigateToForgotPassword = () => {
    navigateWithTracking(FORGOT_PASS_PATH);
  };

  const navigateToV2ForgotPassword = () => {
    navigateV2WithTracking(FORGOT_PASS_PATH);
  };

  const navigateToV2PasswordResetSuccess = (email) => {
    const path = email
      ? `${PASSWORD_RESET_SUCCESS_PATH}?email=${encodeURIComponent(email)}`
      : PASSWORD_RESET_SUCCESS_PATH;
    navigateV2WithTracking(path);
  };

  const navigateToV2PasswordResetComplete = () => {
    navigateV2WithTracking(PASSWORD_RESET_COMPLETE_PATH);
  };

  const navigateToCheckEmail = (options) => {
    navigateWithTracking(CHECK_EMAIL_PATH, options);
  };

  const navigateToNewBrokerConnect = () => {
    navigateWithTracking(WELCOME_CONNECTED_PATH);
  };

  // Profile
  const navigateToAccountOverview = () => {
    navigateWithTracking(ACCOUNT_OVERVIEW_PATH);
  };

  const navigateToV2AccountOverview = () => {
    navigateV2WithTracking(ACCOUNT_OVERVIEW_V2_PATH);
  };

  const navigateToAccountSecurity = () => {
    navigateWithTracking(ACCOUNT_SECURITY_PATH);
  };

  const navigateToV2AccountSecurity = () => {
    navigateV2WithTracking(ACCOUNT_SECURITY_V2_PATH);
  };

  const navigateToAccountTwoFactorAuthentication = () => {
    navigateWithTracking(ACCOUNT_TWO_FACTOR_AUTHENTICATION_PATH);
  };

  const navigateToProfileNotification = () => {
    navigateWithTracking(ACCOUNT_NOTIFICATION_PATH);
  };

  const navigateToReceipts = () => {
    navigateWithTracking(ACCOUNT_PLAN_BILLING_PATH);
  };

  const navigateToV2Receipts = () => {
    navigateV2WithTracking(ACCOUNT_PLAN_BILLING_V2_PATH);
  };

  const navigateToPlans = (options) => {
    navigateV2WithTracking(options || PLANS_PATH);
  };

  const navigateToProfilePayment = () => {
    navigateWithTracking(ACCOUNT_PAYMENT_PATH);
  };

  // Stock Search
  const navigateToStockSearch = (param) => {
    if (param) {
      navigateWithTracking(`${STOCK_SEARCH_PATH}?${param}`);
    } else {
      navigateWithTracking(STOCK_SEARCH_PATH);
    }
  };

  const navigateToStockDetails = (symbol) => {
    navigateWithTracking(STOCK_SEARCH_DETAILS_PATH.replace(":symbol", symbol));
  };

  const navigateToV2StockDetails = (symbol) => {
    navigateV2WithTracking(MARKET_DETAILS_V2_PATH.replace(":symbol", symbol));
  };

  const navigateToResearhStockDetails = (symbol) => {
    navigateV2WithTracking(STOCK_DETAIL_PATH.replace(":symbol", symbol));
  };

  const navigateToStockMovers = () => {
    navigateWithTracking(STOCK_MOVERS_PATH);
  };

  const navigateToV2StockMovers = () => {
    navigateV2WithTracking(MARKET_MOVERS_V2_PATH);
  };

  const navigateToStockFearGreedIndex = () => {
    navigateWithTracking(STOCK_FEAR_GREED_INDEX_PATH);
  };

  const navigateToV2StockFearGreedIndex = () => {
    navigateV2WithTracking(MARKET_FEAR_GREED_INDEX_V2_PATH);
  };

  // Common
  const navigateTo = (path, options) => {
    navigateWithTracking(path, options);
  };
  const navigateToV2 = (path, options) => {
    navigateV2WithTracking(path, options);
  };

  const navigateBack = () => {
    navigate(-1);
  };

  const navigateBackToParent = () => {
    const currentPath = getItemFromStorage(CURRENT_PATH_KEY);
    navigate(currentPath);
  };

  const navigateToHome = () => {
    navigateWithTracking(HOME_PATH);
  };

  const navigateToHostStrategy = (id) => {
    navigateWithTracking(STRATEGY_HOST_PATH.replace(":id", id));
  };

  const navigateToHostStrategyProgress = (strategyId) => {
    navigateWithTracking(
      STRATEGY_HOST_PROGRESS_PATH.replace(":id", strategyId)
    );
  };

  const navigateToHostStrategyForm = (strategyId) => {
    navigateWithTracking(STRATEGY_HOST_FORM_PATH.replace(":id", strategyId));
  };

  // Notification
  const navigateToNotification = () => {
    navigateWithTracking(NOTIFICATION_PATH);
  };

  const navigateToV2Notification = () => {
    navigateV2WithTracking(NOTIFICATION_V2_PATH);
  };

  // Help Center
  const navigateToHelpCenter = (options) => {
    navigateWithTracking(options || HELP_CENTER_PATH);
  };

  const navigateToV2HelpCenter = (options) => {
    navigateV2WithTracking(options || HELP_CENTER_V2_PATH);
  };

  const navigateToV2Partnerships = () => {
    navigate(ACCOUNT_PARTNERSHIPS_V2_PATH);
  };

  // HOST
  const navigateToV2HostDetails = (id) => {
    navigateV2WithTracking(HOST_DETAIL_PATH.replace(":id", id));
  };

  const navigateToHostDashboard = () => {
    navigateV2WithTracking(HOST_DASHBOARD_PATH);
  };

  // Surmount Account
  const navigateToSurmountAccountLanding = () => {
    navigateV2WithTracking(SURMOUNT_ACCOUNT_PATH);
  };

  const navigateToSurmountAccountCreate = () => {
    navigateV2WithTracking(SURMOUNT_ACCOUNT_CREATE_PATH);
  };

  const navigateToSurmountAccountManage = (id) => {
    navigateV2WithTracking(DASHBOARD_SURMOUNT_MANAGE_PATH.replace(":id", id));
  };

  // Signup
  const navigateToSignupInvestmentApproach = () =>
    navigate(SIGNUP_INVESTMENT_APPROACH);

  const navigateToSignupStyleDefining = () =>
    navigate(SIGNUP_INVESTMENT_STYLE_DEFINING);

  const navigateToSignupBuildPortfolio = () => navigate(SIGNUP_BUILD_PORTFOLIO);

  const navigateToSignupChooseStrategies = (queryParams) => {
    if (queryParams) {
      navigate(`${SIGNUP_CHOOSE_STRATEGIES}${queryParams}`);
    } else {
      navigate(SIGNUP_CHOOSE_STRATEGIES);
    }
  };

  const navigateToSignupRecommendations = (type) => {
    if (type) {
      navigate(`${SIGNUP_SURMOUNT_RECOMMENDATIONS}?type=${type}`);
    } else {
      navigate(SIGNUP_SURMOUNT_RECOMMENDATIONS);
    }
  };

  const navigateToSignupRecommendationsWithId = (id) => {
    if (id) {
      navigate(`${SIGNUP_SURMOUNT_RECOMMENDATIONS}?id=${id}`);
    } else {
      navigate(SIGNUP_SURMOUNT_RECOMMENDATIONS);
    }
  };

  const navigateToSignupSurmountAccountPersonalDetails = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_PERSONAL_DETAILS);

  const navigateToSignupSurmountAccountPhoneVerification = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_PHONE_VERIFICATION);

  const navigateToSignupSurmountRecommendationsKycQuestions = () =>
    navigate(SIGNUP_SURMOUNT_RECOMMENDATIONS_KYC_QUESTIONS);

  const navigateToSignupIntroQuestions = (type) => {
    if (type) {
      navigate(`${SIGNUP_INTRO_QUESTIONS}?type=${type}`);
    } else {
      navigate(SIGNUP_INTRO_QUESTIONS);
    }
  };

  const navigateToSignupFundYourAccount = (id) => {
    if (id) {
      navigate(`${SIGNUP_DEPOSIT_FUNDS}?id=${id}`);
    } else {
      navigate(SIGNUP_DEPOSIT_FUNDS);
    }
  };

  const navigateToSignupDepositSuccess = () => {
    navigate(SIGNUP_DEPOSIT_SUCCESS);
  };

  const navigateToSignupConnectBrokerAccount = () =>
    navigate(SIGNUP_CONNECT_BROKER_ACCOUNT);

  const navigateToSignupConnectedBrokerAccount = () =>
    navigate(SIGNUP_CONNECTED_BROKER_ACCOUNT);

  const navigateToSignupCreateSurmountAccount = () =>
    navigate(SIGNUP_CREATE_SURMOUNT_ACCOUNT);

  const navigateToSignupGetStarted = (id) =>
    navigate(SIGNUP_GET_STARTED.replace(":id", id));

  const navigateToSignupFundingMethod = (queryParams) => {
    if (queryParams) {
      navigate(`${SIGNUP_CHOOSE_FUNDING_METHOD}${queryParams}`);
    } else {
      navigate(SIGNUP_CHOOSE_FUNDING_METHOD);
    }
  };

  const navigateToSignupSurmountAccountVerifyIdentity = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_VERIFY_IDENTITY);

  const navigateToSignupSurmountAccountCountryCitizenship = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_COUNTRY_CITIZENSHIP);

  const navigateToSignupSurmountAccountAddress = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_ADDRESS);

  const navigateToSignupSurmountAccountEmployeeStatus = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_EMPLOYEE_STATUS);

  const navigateToSignupSurmountAccountAnnualIncome = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_ANNUAL_INCOME);

  const navigateToSignupSurmountAccountFundingSourse = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_FUNDING_SOURCE);

  const navigateToSignupSurmountAccountNetWorth = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_NET_WORTH);

  const navigateToSignupSurmountAccountAffiliation = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_AFFILIATION);

  const navigateToSignupSurmountAccountTaxW9Agreement = () =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_TAX_W9_AGREEMENT);

  const navigateToSignupSurmountAccountStatus = (id) =>
    navigate(SIGNUP_SURMOUNT_ACCOUNT_STATUS.replace(":id", id));

  return {
    navigateTo,
    navigateBack,
    navigateBackToParent,
    navigateToSignin,
    navigateToSignup,
    navigateToMarketplace,
    navigateToForgotPassword,
    navigateToDashboard,
    navigateToStrategyList,
    navigateToCheckEmail,
    navigateToStrategyDetail,
    navigateToPortfolioDetail,
    navigateToMarketplaceDetail,
    navigateToStrategyEdit,
    navigateToHostStrategy,
    navigateToHostStrategyProgress,
    navigateToEtfBuilder,
    navigateToNoCodeBuilder,
    navigateToNoCodeBuilderEdit,
    navigateToHostStrategyForm,
    navigateToNotification,
    navigateToHelpCenter,
    navigateToVerifyPhone,
    navigateToWelcome,
    navigateToNewBrokerConnect,
    navigateToProfileNotification,
    navigateToReceipts,
    navigateToPlans,
    navigateToProfilePayment,
    navigateToAccountTwoFactorAuthentication,
    navigateToAccountOverview,
    navigateToAccountPaymentPlanDetail,
    navigateToAccountSecurity,
    navigateToStockDetails,
    navigateToStockSearch,
    navigateToStockMovers,
    navigateToStockFearGreedIndex,
    navigateToV2,
    navigateToHome,
    navigateToV2Marketplace,
    navigateToCollectionDetail,
    navigateToV2ForgotPassword,
    navigateToV2PasswordResetSuccess,
    navigateToV2PasswordResetComplete,
    navigateToV2HelpCenter,
    navigateToV2VerifyPhone,
    navigateToV2NoCodeBuilder,
    navigateToV2Notification,
    navigateToV2StrategyList,
    navigateToV2Welcome,
    navigateToV2WelcomeConnected,
    navigateToV2Receipts,
    navigateToV2AccountOverview,
    navigateToV2AccountSecurity,
    navigateToV2StockMovers,
    navigateToV2StockFearGreedIndex,
    navigateToV2PortfolioDetail,
    navigateToV2MarketplaceDetail,
    navigateToV2EtfBuilder,
    navigateToV2StrategyDetail,
    navigateToV2StrategyEdit,
    navigateToV2NoCodeBuilderEdit,
    navigateToV2Market,
    navigateToV2Research,
    navigateToV2Signin,
    navigateToV2Signup,
    navigateToV2WelcomeQuestions,
    navigateToV2StockDetails,
    navigateToResearhStockDetails,
    navigateToV2Partnerships,
    navigateToV2HostDetails,
    navigateToHostDashboard,
    navigateToSurmountAccountCreate,
    navigateToSurmountAccountLanding,
    navigateToSurmountAccountManage,
    navigateToSignupInvestmentApproach,
    navigateToSignupStyleDefining,
    navigateToSignupBuildPortfolio,
    navigateToSignupChooseStrategies,
    navigateToSignupRecommendations,
    navigateToSignupConnectBrokerAccount,
    navigateToSignupConnectedBrokerAccount,
    navigateToSignupCreateSurmountAccount,
    navigateToSignupGetStarted,
    navigateToSignupFundingMethod,
    navigateToSignupSurmountAccountCountryCitizenship,
    navigateToSignupSurmountAccountAddress,
    navigateToSignupSurmountAccountEmployeeStatus,
    navigateToSignupSurmountAccountAnnualIncome,
    navigateToSignupSurmountAccountFundingSourse,
    navigateToSignupSurmountAccountNetWorth,
    navigateToSignupSurmountAccountAffiliation,
    navigateToSignupSurmountAccountVerifyIdentity,
    navigateToSignupSurmountAccountTaxW9Agreement,
    navigateToSignupSurmountAccountStatus,
    navigateToSignupIntroQuestions,
    navigateToSignupFundYourAccount,
    navigateToSignupDepositSuccess,
    navigateToSignupRecommendationsWithId,
    navigateToSignupSurmountAccountPersonalDetails,
    navigateToSignupSurmountAccountPhoneVerification,
    navigateToSignupSurmountRecommendationsKycQuestions,
  };
}
